import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDebounce} from '../../shared/hooks';
import {createUser, getMe, listLicencasReference, listRolesReference, listUsers} from './UsersService';
import {
    buildUserStats,
    extractListPayload,
    getBackendMessage,
    normalizeReferenceId,
    normalizeUserRows,
    papelOptionsPara,
    perfisDaCriacao,
    PORTAL_ACCESS_TYPE,
    vinculosDaCriacao,
} from './usersUtils';

const createEmptyForm = () => ({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    ddiPhoneNumber: '55',
    mobilePhoneNumber: '',
    licenses: [],
    // Perfis por licença: todo perfil é de uma licença, e só vale nela.
    perfis: {},
    papeis: {},
});

export function usePortalUsers() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('Todos');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [createError, setCreateError] = useState('');
    const [form, setForm] = useState(createEmptyForm());
    const [references, setReferences] = useState({roles: [], licencas: []});
    const [me, setMe] = useState(null);
    const debouncedSearch = useDebounce(search, 400);

    const loadReferences = useCallback(async () => {
        try {
            const [rolesResponse, licencasResponse] = await Promise.all([
                listRolesReference(),
                listLicencasReference(),
            ]);
            setReferences({
                roles: extractListPayload(rolesResponse).items,
                licencas: extractListPayload(licencasResponse).items,
            });
        } catch {
            setReferences({roles: [], licencas: []});
        }
    }, []);

    const loadMe = useCallback(async () => {
        try {
            const response = await getMe();
            setMe(response?.data?.data ?? response?.data?.valor ?? response?.data ?? null);
        } catch {
            setMe(null);
        }
    }, []);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const normalizedFilter = debouncedSearch.trim();
            const isEmailSearch = normalizedFilter.includes('@');
            // Sem `active`/`blocked` na consulta: o identity filtraria pela conta, e no portal a
            // situação que vale é a da assinatura (BBF-67). O filtro de status é feito aqui.
            const response = await listUsers({
                page: 0,
                size: 300,
                username: normalizedFilter && !isEmailSearch ? normalizedFilter : '',
                email: normalizedFilter && isEmailSearch ? normalizedFilter : '',
            });
            setRows(normalizeUserRows(extractListPayload(response).items));
        } catch (loadError) {
            setRows([]);
            setError(getBackendMessage(loadError, 'Não foi possível carregar os usuários.'));
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    useEffect(() => {
        loadReferences();
        loadMe();
    }, [loadReferences, loadMe]);

    const papelOptions = useCallback((licenseId) => papelOptionsPara(me, licenseId), [me]);

    const filteredRows = useMemo(() => {
        if (status === 'Todos') return rows;
        return rows.filter((row) => row.status === status);
    }, [rows, status]);

    // No portal a pessoa está numa assinatura só; com uma licença, ela já vem escolhida.
    const openCreate = useCallback(() => {
        const licencas = references.licencas || [];
        setForm({...createEmptyForm(), licenses: licencas.length === 1 ? [licencas[0]] : []});
        setCreateError('');
        setDialogOpen(true);
    }, [references.licencas]);

    const submitCreate = useCallback(async () => {
        if (!form.username?.trim() || !form.email?.trim()) {
            setCreateError('Usuário e e-mail são obrigatórios.');
            return;
        }
        if (!form.password?.trim()) {
            setCreateError('Senha é obrigatória para criar um usuário.');
            return;
        }
        if (form.password !== form.confirmPassword) {
            setCreateError('Senha e confirmação de senha devem ser iguais.');
            return;
        }

        setSaving(true);
        setCreateError('');
        try {
            await createUser({
                userName: form.username.trim(),
                email: form.email.trim(),
                password: form.password.trim(),
                ddi: form.ddiPhoneNumber?.trim() || null,
                phoneNumber: form.mobilePhoneNumber?.trim() || null,
                accessType: PORTAL_ACCESS_TYPE,
                userNotExpires: true,
                roleIdList: perfisDaCriacao(form.licenses, form.perfis),
                licenseIdList: (form.licenses || []).map(normalizeReferenceId).filter(Boolean),
                vinculos: vinculosDaCriacao(form.licenses, form.papeis),
            });
            setDialogOpen(false);
            setForm(createEmptyForm());
            await loadUsers();
        } catch (requestError) {
            setCreateError(getBackendMessage(requestError, 'Não foi possível criar o usuário.'));
        } finally {
            setSaving(false);
        }
    }, [form, loadUsers]);

    return {
        rows: filteredRows,
        stats: buildUserStats(rows),
        loading,
        error,
        search,
        status,
        setSearch,
        setStatus,
        dialogOpen,
        setDialogOpen,
        saving,
        createError,
        form,
        setForm,
        references,
        papelOptions,
        openCreate,
        submitCreate,
        reload: loadUsers,
    };
}
